import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  FileText, Brain, MessageCircle,
  FileEdit, Target, FileBarChart, Shield, Baby,
  ChevronDown, ChevronUp, Zap, Volume2, Mic,
  Utensils, Activity, Users, CheckCircle,
  ClipboardList, LogOut, RefreshCw, Heart, AlertTriangle,
  ChevronsUp, ChevronsDown
} from 'lucide-react';
import PrimaryButton from '../../common/PrimaryButton';
import { useFeature } from '../../../hooks/useFeature';
import type { AssessmentFormHandle } from './NursingAssessmentForm';
import AdultTransitionToggle from './AdultTransitionToggle';
import { withPediatricFirstConfig, patientTypeLabel } from './assessmentFormConfig';
import ICDSelect from '../../common/ICDSelect';
import { joinIcd10Field, splitIcd10Field } from '../../../utils/icd10Entries';
import { StandardizedTestSection } from './StandardizedTestTable';
import { legacyEntries, pickTherapyTestFields, therapyTestErrors } from './standardizedTests';
import type { TherapyTestFieldsDto } from '../../../dtos/patientmr/assessments/therapy-tests.dto';

// Phase 19 F23: the old free-text test fields; legacy values show read-only as "Earlier entry"
const LEGACY_TEST_KEYS = [
  { key: 'Pediatric Language Development-Standardized Assessment Tool Used', label: 'Standardized Assessment Tool Used' },
  { key: 'Pediatric Language Development-Assessment Scores', label: 'Assessment Scores' },
];

// Assessment Type definitions
type AssessmentType = 'initial' | 'recertification' | 'discharge';

interface AssessmentTypeConfig {
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

interface FieldConfig {
  name: string;
  type: 'text' | 'textarea' | 'select' | 'date' | 'checkbox-group' | 'number';
  required?: boolean;
  placeholder?: string;
  options?: string[];
  showFor?: AssessmentType[];
  readonly?: boolean;
  min?: number;
  max?: number;
}

const ASSESSMENT_TYPES: Record<AssessmentType, AssessmentTypeConfig> = {
  initial: {
    label: 'Initial/Admission Evaluation (SOC)',
    description: 'Comprehensive ST evaluation for Start of Care. All baseline data collected.',
    icon: <ClipboardList size={16} />,
    color: 'blue'
  },
  recertification: {
    label: 'Recertification Evaluation',
    description: 'Periodic reassessment (60-90 days). Focus on progress and continued medical necessity.',
    icon: <RefreshCw size={16} />,
    color: 'amber'
  },
  discharge: {
    label: 'Discharge Evaluation',
    description: 'Final evaluation documenting outcomes, communication status, and recommendations.',
    icon: <LogOut size={16} />,
    color: 'green'
  }
};

interface STAssessmentFormProps {
  editingId: string | null;
  patientId: string;
  onSave: (data: any) => void;
  existingData?: any;
  assessmentStatus?: string;
  isReadOnly?: boolean;
}

const STAssessmentForm = forwardRef<AssessmentFormHandle, STAssessmentFormProps>(
  ({ editingId, patientId, onSave, existingData = null, assessmentStatus, isReadOnly = false }, ref) => {
  // Phase 19 F26: face-to-face encounter fields are Medicare content, hidden unless the medicareContent flag is on (as PT/OT)
  const medicareEnabled = useFeature('medicareContent');
  // Default form configuration
  const defaultFormConfig = {
    assessmentType: 'initial' as AssessmentType,
    patientType: 'pediatric', // Phase 19 F26: 'pediatric' or 'adult' (transition, 18–21 toggle)
    settingType: 'home-health', // Phase 19 F26: always home health (no selector)
    primaryFocus: 'speech-language', // 'speech-language', 'swallowing', 'cognitive', 'voice'
    showOptionalSections: false
  };

  // Helper to normalize assessment type from backend format to internal format
  const normalizeAssessmentType = (type: string): AssessmentType => {
    const typeMap: Record<string, AssessmentType> = {
      'START_OF_CARE': 'initial',
      'start_of_care': 'initial',
      'initial': 'initial',
      'REASSESSMENT': 'recertification',
      'reassessment': 'recertification',
      'recertification': 'recertification',
      'END_OF_CARE': 'discharge',
      'end_of_care': 'discharge',
      'discharge': 'discharge'
    };
    return typeMap[type] || 'initial';
  };

  // Form configuration state - MVP defaults to pediatric for home health
  const [formConfig, setFormConfig] = useState(() => {
    // If editing existing data with formConfig, use that immediately
    if (existingData && existingData.formConfig) {
      const config = withPediatricFirstConfig({ ...defaultFormConfig, ...existingData.formConfig });
      // Normalize assessment type from backend format to internal format
      config.assessmentType = normalizeAssessmentType(config.assessmentType);
      return config;
    }
    // Otherwise use defaults for new form
    return defaultFormConfig;
  });

  // Medical codes state
  const [secondaryICD10Codes, setSecondaryICD10Codes] = useState<string[]>([]);
  const [selectedSecondaryICD10, setSelectedSecondaryICD10] = useState<string>('');

  // Track if initial data has been loaded to prevent overwriting
  const initialLoadCompleteRef = useRef(false);

  // Collapsible sections state
  const [configSectionCollapsed, setConfigSectionCollapsed] = useState(false);
  const [collapsedSections, setCollapsedSections] = useState({
    'Recertification Summary': true,
    'Discharge Summary': true,
    'Diagnosis and Coding': true,
    'Functional Limitations': true,
    'Home Health Certification': true,
    'Physician Orders and Certification': true,
    'Patient History': true,
    'Mental Status': true,
    'Pediatric Information': true,
    'Pediatric Language Development': true,
    'Speech Assessment': true,
    'Language Assessment': true,
    'Pediatric PDN Speech Therapy': true,
    'Swallowing Assessment': true,
    'Cognitive-Communication': true,
    'Voice Assessment': true,
    'Fluency Assessment': true,
    'AAC Assessment': true,
    'Caregiver/Communication Partners': true,
    'Treatment Plan': true,
    'ST Attestation': true,
    'Standardized Tests': true
  });

  // Track if all sections are collapsed for toggle button
  const [, setAllSectionsCollapsed] = useState(true);

  useEffect(() => {
    // Dynamically check all currently rendered categories
    const allCategoryNames = getAssessmentCategories().map(cat => cat.name);
    const allCollapsed = allCategoryNames.every(
      (name) => collapsedSections[name] === true
    );
    setAllSectionsCollapsed(allCollapsed);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- getAssessmentCategories is recreated every render; the listed deps are the inputs that change the categories
  }, [collapsedSections, formConfig]);


  // Listen for toggle all sections events from parent
  useEffect(() => {
    const handleToggleAll = (event: any) => {
      toggleAllSections(event.detail.collapse);
    };
    window.addEventListener('toggleAllSections', handleToggleAll);
    return () => window.removeEventListener('toggleAllSections', handleToggleAll);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- toggleAllSections is recreated every render; the listener is re-registered when collapsedSections changes
  }, [collapsedSections]);



  // Helper to check if a field should be visible based on assessment type
  const shouldShowField = (field: FieldConfig): boolean => {
    if (!field.showFor) return true;
    return field.showFor.includes(formConfig.assessmentType);
  };

  // Helper to get dynamic placeholder based on assessment type
  const getPlaceholder = (field: FieldConfig): string => {
    const { assessmentType } = formConfig;
    
    if (assessmentType === 'recertification') {
      const recertPlaceholders: Record<string, string> = {
        'Chief Complaint': 'Current primary concerns or changes since last evaluation',
        'History of Present Problem': 'Update on current condition and any changes since last evaluation',
        'Communication Functional Limitations': 'Current limitations - note any changes from baseline',
        'Treatment Goals': 'Updated goals for this certification period',
        'ST Frequency': 'e.g., Continue 2x/week OR Change to 1x/week',
        'Frequency Justification': 'Justify continued or changed frequency based on current status'
      };
      if (recertPlaceholders[field.name]) return recertPlaceholders[field.name];
    }
    
    if (assessmentType === 'discharge') {
      const dischargePlaceholders: Record<string, string> = {
        'Communication Functional Limitations': 'Communication status at discharge compared to admission',
        'Treatment Goals': 'Final goal status - Met/Partially Met/Not Met for each goal',
        'ST Summary': 'Summary of interventions provided and patient response during episode of care'
      };
      if (dischargePlaceholders[field.name]) return dischargePlaceholders[field.name];
    }
    
    return field.placeholder || `Enter ${field.name}...`;
  };

  // Get assessment categories based on configuration
  const getAssessmentCategories = () => {
    const categories = [];
    const { assessmentType } = formConfig;

    // ========== RECERTIFICATION SUMMARY (only for recert) ==========
    if (assessmentType === 'recertification') {
      categories.push({
        name: "Recertification Summary",
        priority: 0.5,
        required: true,
        description: "Document progress since last evaluation and justification for continued ST services.",
        fields: [
          { name: "Previous Evaluation Date", type: "date", required: true },
          { name: "Recertification Period Start", type: "date", required: true },
          { name: "Recertification Period End", type: "date", required: true },
          { name: "Changes Since Last Evaluation", type: "textarea", required: true,
            placeholder: "Document all changes in patient's communication, swallowing, or cognitive status since the last ST evaluation." },
          { name: "Progress Toward Goals", type: "textarea", required: true,
            placeholder: "For each treatment goal, document:\n- Goal description\n- Baseline measurement\n- Current measurement\n- Progress status (Met, Partially Met, Not Met, Ongoing)" },
          { name: "Continued Medical Necessity Justification", type: "textarea", required: true,
            placeholder: "Explain why continued skilled ST services are medically necessary. Include: ongoing communication/swallowing deficits, safety concerns, skilled intervention requirements, and expected outcomes." },
          { name: "Barriers to Progress", type: "textarea",
            placeholder: "Document any barriers to achieving goals (medical complications, cognitive limitations, compliance issues, etc.)" },
          { name: "Plan Modifications", type: "textarea",
            placeholder: "Document any changes to treatment plan, goals, or frequency for the next certification period" },
          { name: "Physician Contact for Recertification", type: "select", required: true,
            options: ["Physician contacted - verbal orders obtained", "Physician contacted - awaiting response", "Face-to-face encounter completed", "Telehealth encounter completed", "Orders received via fax/portal"] },
          { name: "Physician Contact Date", type: "date" },
          { name: "Updated Orders Received", type: "select",
            options: ["No changes to orders", "Orders updated - see details", "Pending physician signature"] },
          { name: "Order Changes Description", type: "textarea", placeholder: "If orders were updated, describe the changes" }
        ]
      });
    }

    // ========== DISCHARGE SUMMARY (only for discharge) ==========
    if (assessmentType === 'discharge') {
      categories.push({
        name: "Discharge Summary",
        priority: 0.5,
        required: true,
        description: "Complete discharge documentation including communication outcomes and recommendations.",
        fields: [
          { name: "Discharge Date", type: "date", required: true },
          { name: "Original Start of Care Date", type: "date", required: true },
          { name: "Total Visits Provided", type: "text", placeholder: "e.g., 20 visits over 5 months" },
          { name: "Discharge Reason", type: "select", required: true,
            options: ["Goals met - ST no longer needed", "Goals partially met - maximum benefit achieved", "Patient/family request", "Moved out of service area", 
                      "Transferred to outpatient ST", "Transferred to another agency", "Hospitalization", "Medical decline - ST on hold",
                      "Non-compliance with treatment", "Physician discharged from ST", "Insurance/authorization ended", "Patient deceased", "Other - see notes"] },
          { name: "Discharge Reason Details", type: "textarea", placeholder: "Provide additional details about the discharge reason" },
          { name: "Goals Met Summary", type: "textarea", required: true,
            placeholder: "For each treatment goal, document final status:\n- Goal 1: [Met/Partially Met/Not Met] - Baseline: ___ Current: ___\n- Goal 2: [Met/Partially Met/Not Met] - Baseline: ___ Current: ___" },
          { name: "Communication Outcomes Summary", type: "textarea", required: true,
            placeholder: "Compare communication status at discharge to admission:\n- Speech: Admission ___ → Discharge ___\n- Language: Admission ___ → Discharge ___\n- Swallowing: Admission ___ → Discharge ___" },
          { name: "Discharge Communication Status", type: "select", required: true,
            options: ["Functional communication achieved", "Improved but not functional", "Maintained baseline", "Declined from admission", "Using AAC successfully", "Using compensatory strategies"] },
          { name: "Discharge Instructions Provided", type: "select", required: true,
            options: ["Yes - verbal and written HEP provided", "Yes - verbal instructions only", "Yes - written HEP only", 
                      "No - patient declined", "No - patient unable to receive", "N/A - patient deceased"] },
          { name: "Home Program Details", type: "textarea",
            placeholder: "List communication activities and exercises included in discharge home program" },
          { name: "Patient/Caregiver Verbalized Understanding", type: "select", required: true,
            options: ["Yes - demonstrated full understanding", "Yes - with some clarification needed", "Partial understanding - reinforced teaching", 
                      "Limited understanding - referred to physician", "Unable to assess", "N/A - patient deceased"] },
          { name: "AAC/Equipment at Discharge", type: "textarea",
            placeholder: "List communication devices/equipment patient has at discharge (AAC device, communication board, etc.)" },
          { name: "Diet Level at Discharge", type: "select",
            options: ["Regular diet", "Mechanical soft", "Minced and moist", "Pureed", "Enteral only", "NPO", "N/A - no dysphagia"] },
          { name: "Recommendations", type: "textarea", required: true,
            placeholder: "Recommendations for continued care:\n- Continue home program independently\n- Outpatient ST referral\n- School-based services\n- AAC follow-up" },
          { name: "Follow-up Appointments", type: "textarea", placeholder: "Scheduled follow-up appointments or referrals" },
          { name: "ST Discharge Summary", type: "textarea", required: true,
            placeholder: "Comprehensive narrative summary of the patient's ST episode: reason for referral, interventions provided, patient response, and current communication status." },
          { name: "Physician Notified of Discharge", type: "select", required: true,
            options: ["Yes - verbal notification", "Yes - written/fax notification", "Yes - via EMR/portal", "Pending notification", "N/A - physician ordered discharge"] },
          { name: "Physician Notification Date", type: "date" },
          { name: "Receiving Agency Name", type: "text",
            placeholder: "Name of agency/facility patient is transferring to (if applicable)" },
          { name: "Receiving Agency Contact", type: "text",
            placeholder: "Phone number or contact person at receiving agency" },
          { name: "Continuity of Care Notes", type: "textarea",
            placeholder: "Critical information for receiving provider: current care needs, equipment requirements, caregiver training status, pending issues" },
          { name: "Services Provided Summary", type: "textarea",
            placeholder: "Total visits/hours by discipline during episode" }
        ]
      });
    }

    // ========== DIAGNOSIS AND CODING (all types) ==========
    categories.push({
    name: "Diagnosis and Coding",
    priority: 1,
    required: true,
    fields: [
    { name: "Primary ICD-10 Code", type: "select", required: true },
    { name: "Secondary ICD-10 Codes", type: "custom-icd10-list" as any },
    { name: "Medical Necessity - Clinical Complexity", type: "textarea", required: true,
    showFor: ['initial', 'recertification'] as AssessmentType[],
    placeholder: "Describe the patient's medical condition and complexity. Include:\n• Primary diagnosis and relevant comorbidities\n• Medical history affecting therapy needs\n• Why this condition requires skilled therapy (not routine/maintenance)\n• Technology dependence (ventilator, trach, feeding tube, etc.)" },
      { name: "Medical Necessity - Skilled Care Requirements", type: "textarea", required: true,
          showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Explain WHY skilled therapy is required:\n• Specific skilled techniques needed (not routine exercises)\n• Clinical judgment required for assessment/modification\n• Safety monitoring needed during treatment\n• Complex presentation requiring therapist expertise\n• Why caregiver or aide cannot provide this service" },
          { name: "Medical Necessity - Functional Impact", type: "textarea", required: true,
            showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Describe how impairments affect patient's function:\n• ADL limitations\n• Mobility/communication limitations\n• Safety risks without therapy\n• Impact on caregiver burden" },
          { name: "Medical Necessity - Expected Outcomes", type: "textarea", required: true,
            showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "What functional improvements are expected with therapy?\n• Specific measurable outcomes\n• Timeframe for anticipated progress\n• How therapy will reduce hospitalizations/complications" }
        ]
      });

    // ========== FUNCTIONAL LIMITATIONS (CMS-485 Section 8) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Functional Limitations",
        priority: 1.5,
        required: true,
        showFor: ['initial', 'recertification'] as AssessmentType[],
        description: "CMS-485 Section 8: Select all functional limitations that apply (at least one required for medical necessity)",
        fields: [
          { name: "Functional Limitation Categories", type: "checkbox-group", required: true,
            options: [
              "A - Amputation",
              "B - Bowel/Bladder (Incontinence)",
              "C - Contracture",
              "D - Hearing",
              "E - Paralysis",
              "F - Endurance",
              "G - Ambulation",
              "H - Speech",
              "I - Inadequate Nutrition",
              "J - Confusion",
              "K - Dyspnea",
              "L - Other (Specify)"
            ],
            placeholder: "Check at least one criterion" },
          { name: "Supportive Devices Required", type: "text",
            placeholder: "Specify devices: walker, wheelchair, oxygen, ventilator, etc." },
          { name: "Special Transportation Details", type: "text",
            placeholder: "Describe transport needs: medical van, stretcher transport, ventilator-equipped vehicle" },
          { name: "Assistance Required", type: "textarea",
            placeholder: "Describe assistance needed: supervision, physical assist, continuous skilled nursing, cannot be left unattended" 
          },
          { name: "Other Functional Limitation Description", type: "textarea",
            placeholder: "If 'L - Other' selected above, describe the specific functional limitation" },
          { name: "Functional Limitation Impact", type: "textarea", required: true,
            placeholder: "Describe how these functional limitations affect patient's daily activities and safety. Explain why skilled ST is necessary to address these limitations." }
        ]
      });
    }

    // ========== HOME HEALTH CERTIFICATION (home health, initial and recert only) ==========
    if (formConfig.settingType === 'home-health' && assessmentType !== 'discharge') {
      categories.push({
        name: "Home Health Certification",
        priority: 2,
        required: true,
        description: assessmentType === 'recertification' 
          ? "Recertification of homebound status and continued ST need."
          : "Homebound status and ST service recommendations.",
        fields: [
          { name: "Homebound Status Criteria (Check all that apply)", type: "checkbox-group", required: true,
            options: [
              "Patient leaves home infrequently and with considerable and taxing effort",
              "Patient requires supportive devices",
              "Patient requires special transportation",
              "Patient requires assistance of another person",
              "Normal inability to leave home due to medical condition",
              "Physician has determined leaving home is medically contraindicated"
            ]
          },
          { name: "Homebound Status Justification", type: "textarea", required: true,
            placeholder: "Document specific reasons patient meets homebound criteria. Include: medical equipment dependency, cognitive/communication limitations, aspiration risk, safety concerns, or functional deficits preventing community access." },
          { name: "Referring Physician", type: "text", required: assessmentType === 'initial',
            showFor: ['initial'] as AssessmentType[],
            placeholder: "Physician name who referred/ordered ST services" },
          { name: "Referring Physician NPI", type: "text",
            showFor: ['initial'] as AssessmentType[],
            placeholder: "10-digit NPI number" },
          { name: "Start of Care Date", type: "date", required: true, showFor: ['initial'] as AssessmentType[] },
          { name: "Recommended ST Frequency", type: "text", required: true, 
            placeholder: "e.g., 2x/week for 8 weeks, then 1x/week for 4 weeks" },
          { name: "Frequency Justification", type: "textarea", required: true, 
            placeholder: "Explain why this frequency is medically necessary based on patient's communication/swallowing deficits and goals" },
          { name: "Anticipated Certification Period", type: "select", required: true,
            options: ["60 days (standard)", "30 days", "90 days", "Other - specify in notes"] }
        ]
      });
    }

    // ========== PHYSICIAN ORDERS AND CERTIFICATION (all types) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Physician Orders and Certification",
        priority: 2.5,
        required: true,
        showFor: ['initial', 'recertification'] as AssessmentType[],
        description: "Track physician orders and certification requirements (CMS-485 Sections 4 & 5)",
        fields: [
          { name: "Physician Order Date", type: "date", required: true,
            placeholder: "Date physician orders were received" },
          { name: "Physician Order Type", type: "select", required: true,
            options: [
              "Written order received",
              "Verbal order received - signature pending",
              "Telephone order received",
              "Electronic order via EMR/portal",
              "Fax order received"
            ]
          },
          { name: "Verbal Order Date", type: "date",
            placeholder: "If verbal/telephone order, date it was received" },
          { name: "Verbal Order Received By", type: "text",
            placeholder: "Clinician name who received verbal order" },
          { name: "Physician Signature Status", type: "select", required: true,
            options: [
              "Signature obtained",
              "Signature pending - within 30 days",
              "Signature pending - overdue (>30 days)",
              "Electronic signature obtained",
              "N/A - written order received"
            ]
          },
          { name: "Physician Signature Date", type: "date",
            placeholder: "Date physician signed the plan of care" },
          ...(medicareEnabled ? [
          { name: "Face-to-Face Encounter Date", type: "date",
            placeholder: "Date of physician face-to-face encounter (required within 90 days)" },
          { name: "Face-to-Face Encounter Type", type: "select",
            options: [
              "In-person physician visit",
              "Telehealth encounter",
              "Acute/post-acute facility visit",
              "Pending - within allowed timeframe",
              "N/A - not required for this certification"
            ]
          },
          { name: "Face-to-Face Documentation", type: "textarea",
            placeholder: "Summary of face-to-face encounter and how it relates to ST need (if applicable)" }
          ] : []),
          { name: "Certification Period Start Date", type: "date", required: true },
          { name: "Certification Period End Date", type: "date", required: true },
          { name: "Recertification Due Date", type: "date",
            placeholder: "Date next recertification evaluation is due" }
        ]
      });
    }

    // ========== PATIENT HISTORY (all types but abbreviated for recert/discharge) ==========
    categories.push({
      name: "Patient History",
      priority: 3,
      required: true,
      description: assessmentType === 'recertification' ? "Update patient history. Focus on changes since last evaluation." : undefined,
      fields: [
        { name: "Chief Complaint", type: "textarea", required: assessmentType !== 'discharge',
          placeholder: "Primary communication or swallowing concern" },
        { name: "History of Present Problem", type: "textarea", required: assessmentType === 'initial',
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Onset, progression, and current impact of communication/swallowing difficulties" },
        { name: "Past Medical History", type: "textarea", showFor: ['initial'] as AssessmentType[],
          placeholder: "Relevant medical conditions affecting speech, language, or swallowing" },
        { name: "Surgical History", type: "textarea", showFor: ['initial'] as AssessmentType[],
          placeholder: "Relevant surgeries (head/neck, cardiac, neurological)" },
        { name: "Current Medications", type: "textarea", 
          placeholder: "Medications affecting communication, cognition, or swallowing" },
        { name: "Previous ST Services", type: "textarea", showFor: ['initial'] as AssessmentType[],
          placeholder: "Prior speech therapy history and outcomes" },
        { name: "Precautions/Contraindications", type: "textarea", required: true,
          placeholder: "Aspiration precautions, NPO status, behavioral concerns, positioning restrictions" }
      ]
    });

    // ========== MENTAL STATUS (CMS-485 Section 10) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Mental Status",
        priority: 3.8,
        required: true,
        showFor: ['initial', 'recertification'] as AssessmentType[],
        description: "CMS-485 Section 10: Patient's cognitive and mental status",
        fields: [
          { name: "Mental Status Indicators", type: "checkbox-group", required: true,
            options: [
              "Alert and oriented",
              "Oriented to person",
              "Oriented to place",
              "Oriented to time",
              "Comatose",
              "Lethargic",
              "Forgetful",
              "Depressed",
              "Anxious",
              "Agitated",
              "Confused",
              "Combative",
              "Disoriented",
              "Poor judgment",
              "Impaired decision-making",
              "Short-term memory deficit",
              "Long-term memory deficit"
            ]
          },
          { name: "Orientation Level", type: "select", required: true,
            options: [
              "Oriented x 4 (person, place, time, situation)",
              "Oriented x 3 (person, place, time)",
              "Oriented x 2 (person, place)",
              "Oriented x 1 (person only)",
              "Disoriented",
              "Unable to assess"
            ]
          },
          { name: "Mental Status Description", type: "textarea", required: true,
            placeholder: "Describe patient's cognitive function, memory, attention span, ability to follow multi-step directions, safety awareness, and how mental status affects therapy participation and functional independence." },
          { name: "Mental Status Impact on Therapy", type: "textarea",
            placeholder: "Explain how cognitive status affects treatment planning, patient education, safety, and expected outcomes. Include any adaptations needed for therapy interventions." }
        ]
      });
    }

    // ========== PEDIATRIC INFORMATION (pediatric patients) ==========
    if (formConfig.patientType === 'pediatric') {
      categories.push({
        name: "Pediatric Information",
        priority: 3.2,
        required: true,
        description: assessmentType === 'recertification' ? "Update pediatric information. Birth history fields are optional." : undefined,
        fields: [
          { name: "Chronological Age", type: "text", required: true, placeholder: "e.g., 2 years 8 months" },
          { name: "Adjusted/Corrected Age", type: "text", placeholder: "If premature, corrected age for developmental assessment" },
          { name: "Gestational Age at Birth", type: "text", showFor: ['initial'] as AssessmentType[], placeholder: "e.g., 30 weeks" },
          { name: "Birth Weight", type: "text", showFor: ['initial'] as AssessmentType[], placeholder: "e.g., 1400 grams" },
          { name: "Birth History", type: "textarea", showFor: ['initial'] as AssessmentType[],
            placeholder: "Include NICU stay, intubation history, birth complications, early feeding issues" },
          { name: "Primary Caregiver", type: "text", required: assessmentType !== 'discharge', placeholder: "Name and relationship to patient" },
          { name: "Primary Language(s) in Home", type: "text", required: true, placeholder: "Languages spoken in the home" },
          { name: "Current School/Daycare", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Not applicable (infant/toddler)", "Daycare", "Preschool", "Elementary school", "Homebound", "Early intervention program", "Special education"] },
          { name: "IEP/IFSP Status", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["No IEP/IFSP", "IFSP in place (0-3 years)", "IEP in place (3+ years) - includes speech", "IEP in place - no speech services", "Evaluation pending", "Not eligible"] },
          { name: "Other Therapies Receiving", type: "textarea", placeholder: "PT, OT, ABA, etc. with frequency" }
        ]
      });
    }

    // ========== PEDIATRIC LANGUAGE DEVELOPMENT (pediatric patients, all types) ==========
    if (formConfig.patientType === 'pediatric') {
      categories.push({
        name: "Pediatric Language Development",
        priority: 3.5,
        required: true,
        description: assessmentType === 'discharge' ? "Final developmental/language status at discharge." : "Comprehensive language and communication development assessment.",
        fields: [
          { name: "Overall Communication Level", type: "select", required: true,
            options: ["Age-appropriate communication", "Mild delay", "Moderate delay", "Significant delay", "Severe delay", "Non-verbal/Pre-verbal"] },
          { name: "Receptive Language Age Equivalent", type: "text", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "e.g., 18 months" },
          { name: "Expressive Language Age Equivalent", type: "text", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "e.g., 12 months" },
          
          // Receptive Language Details
          { name: "Receptive Language Level", type: "select", required: true,
            options: ["Age-appropriate", "Mild delay", "Moderate delay", "Significant delay", "Severe delay", "Unable to assess"] },
          { name: "Auditory Comprehension", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Responds to name", "Follows simple 1-step commands", "Follows 2-step commands", "Understands simple questions", "Understands complex questions", "Age-appropriate comprehension", "Significant difficulty"] },
          { name: "Receptive Vocabulary", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Age-appropriate", "Mildly limited", "Moderately limited", "Severely limited", "Unable to assess"] },
          { name: "Receptive Language Notes", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Describe receptive language abilities and challenges" },
          
          // Expressive Language Details
          { name: "Expressive Language Level", type: "select", required: true,
            options: ["Age-appropriate", "Mild delay", "Moderate delay", "Significant delay", "Severe delay", "Unable to assess"] },
          { name: "Current Expressive Communication", type: "select", required: true,
            options: ["Pre-intentional communication", "Intentional communication (gestures, vocalizations)", "Single words", "2-word combinations", "3+ word phrases", "Sentences", "Age-appropriate conversation"] },
          { name: "Expressive Vocabulary Size", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["No words", "1-10 words", "10-50 words", "50-100 words", "100+ words", "Age-appropriate vocabulary", "Unable to estimate"] },
          { name: "MLU (Mean Length of Utterance)", type: "text", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "e.g., 1.5 morphemes" },
          { name: "Grammar/Syntax", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Age-appropriate", "Emerging sentence structure", "Delayed grammar development", "Significant grammatical errors", "N/A - not yet at sentence level"] },
          { name: "Expressive Language Notes", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Describe expressive language abilities and challenges" },
          
          // Pragmatics/Social Communication
          { name: "Pragmatic Skills", type: "select", required: true,
            options: ["Age-appropriate social communication", "Mild pragmatic difficulties", "Moderate pragmatic difficulties", "Significant social communication deficits", "ASD characteristics present"] },
          { name: "Joint Attention", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Present and functional", "Emerging", "Inconsistent", "Absent/minimal", "N/A - age appropriate"] },
          { name: "Eye Contact", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Age-appropriate", "Inconsistent", "Limited", "Avoids eye contact"] },
          { name: "Turn-Taking", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Age-appropriate", "Emerging", "Difficulty with turn-taking", "Unable to take turns"] },
          { name: "Play Skills", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Age-appropriate play", "Functional play only", "Limited play repertoire", "Repetitive/restricted play", "Not engaging in play"] },
          
          { name: "Communication Summary", type: "textarea", required: true,
            placeholder: "Summary of primary communication concerns and functional impact" }
        ]
      });
    }

    // ========== SPEECH ASSESSMENT (all types) ==========
    categories.push({
      name: "Speech Assessment",
      priority: 4,
      required: true,
      description: assessmentType === 'discharge' ? "Compare to baseline/admission status." : undefined,
      fields: [
        { name: "Speech Intelligibility", type: "select", required: true,
          options: formConfig.patientType === 'pediatric'
            ? ["Age-appropriate intelligibility", "Reduced intelligibility - familiar listeners understand", "Reduced intelligibility - unfamiliar listeners difficulty", "Severely reduced - context needed", "Unintelligible to most listeners", "Non-verbal"]
            : ["100% intelligible", "95-99% intelligible", "75-94% intelligible", "50-74% intelligible", "25-49% intelligible", "Less than 25% intelligible", "Unintelligible"] },
        { name: "Articulation", type: "select", required: true,
          options: ["Age-appropriate", "Mild impairment", "Moderate impairment", "Severe impairment", "Profound impairment"] },
        { name: "Phonological Processes", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: formConfig.patientType === 'pediatric' 
            ? "List active phonological processes (e.g., final consonant deletion, cluster reduction, fronting, stopping)"
            : "List speech sound errors or patterns" },
        { name: "Oral Motor Function", type: "select", required: true,
          options: ["Normal/Age-appropriate", "Mild weakness/incoordination", "Moderate weakness/incoordination", "Severe weakness", "Unable to assess"] },
        { name: "Oral Motor Details", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Describe lip, tongue, jaw strength and coordination" },
        { name: "Speech Rate", type: "select",
          options: ["Normal rate", "Slow rate", "Rapid rate", "Variable rate", "Scanning speech"] },
        { name: "Prosody", type: "select",
          options: ["Appropriate prosody", "Monotone", "Excessive variation", "Inappropriate stress patterns", "Robotic quality"] },
        { name: "Voice Quality", type: "select",
          options: ["Normal voice", "Hoarse", "Breathy", "Strained", "Weak", "Harsh", "Wet/gurgly"] },
        { name: "Speech Notes", type: "textarea",
          placeholder: "Additional speech observations" }
      ]
    });

    // ========== LANGUAGE ASSESSMENT (adult/geriatric, all types) ==========
    if (formConfig.patientType !== 'pediatric') {
      categories.push({
        name: "Language Assessment",
        priority: 5,
        required: true,
        fields: [
          { name: "Receptive Language", type: "select", required: true,
            options: ["Within normal limits", "Mild impairment", "Moderate impairment", "Severe impairment", "Profound impairment"] },
          { name: "Expressive Language", type: "select", required: true,
            options: ["Within normal limits", "Mild impairment", "Moderate impairment", "Severe impairment", "Profound impairment"] },
          { name: "Auditory Comprehension", type: "select",
            options: ["Within normal limits", "Follows simple commands", "Follows 2-step commands", "Difficulty with complex commands", "Severely impaired comprehension"] },
          { name: "Verbal Expression", type: "select",
            options: ["Fluent speech", "Non-fluent speech", "Anomic - word finding difficulty", "Severely limited verbal output", "Mute/non-verbal"] },
          { name: "Naming", type: "select",
            options: ["Within normal limits", "Mild anomia", "Moderate anomia", "Severe anomia"] },
          { name: "Reading", type: "select",
            options: ["Within normal limits", "Mild difficulty", "Moderate difficulty", "Severe difficulty", "Unable to read", "Not assessed"] },
          { name: "Writing", type: "select",
            options: ["Within normal limits", "Mild difficulty", "Moderate difficulty", "Severe difficulty", "Unable to write", "Not assessed"] },
          { name: "Aphasia Type", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["No aphasia", "Broca's (expressive)", "Wernicke's (receptive)", "Global", "Anomic", "Conduction", "Transcortical motor", "Transcortical sensory", "Mixed"] },
          { name: "Language Notes", type: "textarea",
            placeholder: "Additional language observations" }
        ]
      });
    }

    // ========== PEDIATRIC PDN SPEECH THERAPY (pediatric + home health) ==========
    if (formConfig.patientType === 'pediatric' && formConfig.settingType === 'home-health') {
      categories.push({
        name: "Pediatric PDN Speech Therapy",
        priority: 5.5,
        required: true,
        description: assessmentType === 'discharge' 
          ? "Final status for PDN ST considerations."
          : "Texas Medicaid PDN-specific ST documentation.",
        fields: [
          // Medical Complexity
          { name: "Tracheostomy Status", type: "select", required: true,
            options: assessmentType === 'discharge'
              ? ["No tracheostomy", "Tracheostomy present at discharge", "Tracheostomy decannulated during episode"]
              : ["No tracheostomy", "Tracheostomy present - affects voicing", "Tracheostomy present - speaking valve candidate", "Tracheostomy present - uses speaking valve", "Recently decannulated"] },
          { name: "Speaking Valve Status", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["N/A - no tracheostomy", "Using Passy-Muir valve successfully", "Trialing speaking valve", "Not appropriate candidate", "Physician evaluation needed"] },
          { name: "Ventilator Status", type: "select", required: true,
            options: assessmentType === 'discharge'
              ? ["No ventilator", "Ventilator dependent at discharge", "Weaned from ventilator during episode"]
              : ["No ventilator", "Ventilator dependent - affects communication", "Ventilator dependent - nocturnal only", "Weaning in progress"] },
          { name: "Communication with Trach/Vent", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Describe how patient communicates with trach/vent (gestures, vocalizations, AAC, etc.)" },
          
          // Feeding/Swallowing in PDN Context
          { name: "Feeding Status", type: "select", required: true,
            options: ["Oral feeding only", "G-tube with oral trials", "G-tube only - no oral", "NG tube", "TPN", "NPO - aspiration risk"] },
          { name: "Oral Feeding Safety", type: "select", required: assessmentType !== 'discharge',
            showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Safe for oral feeding", "Safe with modifications/supervision", "Aspiration risk - modified diet", "High aspiration risk - limited oral", "NPO recommended"] },
          { name: "Diet Texture", type: "select",
            options: ["Regular/Age-appropriate", "Soft solids", "Mashed/Ground", "Pureed", "Thickened liquids", "Thin liquids only", "NPO"] },
          { name: "Feeding Precautions", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Describe feeding precautions, positioning requirements, pacing needs" },
          
          // Medical Stability
          { name: "Medical Stability for ST", type: "select", required: assessmentType !== 'discharge',
            showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Medically stable - full ST activities", "Stable with precautions - modified activities", "Unstable - ST on hold", "Unstable - bedside ST only"] },
          { name: "ST Precautions Related to Medical Status", type: "textarea",
            showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Oxygen requirements, positioning restrictions, fatigue management, seizure precautions" },
          
          // AAC Needs
          { name: "AAC Status", type: "select", required: true,
            options: ["No AAC needed - verbal communication", "AAC evaluation recommended", "Low-tech AAC in use", "High-tech AAC in use", "AAC training in progress", "AAC abandoned - reassess"] },
          { name: "Current AAC System", type: "textarea",
            placeholder: "Describe AAC system if in use (device type, vocabulary, access method)" },
          { name: "AAC Recommendations", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "AAC evaluation needs, device recommendations, vocabulary needs" },
          
          // Caregiver Training
          { name: "Caregiver ST Skill Level", type: "select", required: assessmentType !== 'discharge',
            showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Excellent - independent with HEP and strategies", "Good - needs occasional cueing", "Fair - needs regular supervision/training", "Poor - significant training needed", "Unable to participate in ST activities"] },
          { name: "Caregiver Training Provided", type: "textarea",
            placeholder: "Training provided: language stimulation, feeding strategies, AAC use, communication strategies" },
          { name: "Caregiver Return Demonstration", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Successful demonstration of all skills", "Partial demonstration - needs practice", "Unable to demonstrate - reteach needed", "Not applicable"] },
          
          // ST in PDN Context
          { name: "ST Visit Frequency Requested", type: "text", required: assessmentType !== 'discharge',
            showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "e.g., 2x/week for 45 min sessions" },
          { name: "ST Frequency Justification", type: "textarea", required: assessmentType !== 'discharge',
            showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "Justify ST frequency based on: communication needs, swallowing safety, caregiver training needs, AAC needs" },
          { name: "Coordination with PDN Staff", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
            placeholder: "ST activities to be carried over by PDN nurses (feeding strategies, communication opportunities, AAC use)" }
        ]
      });
    }

    // ========== SWALLOWING ASSESSMENT (all types, expanded if primary focus) ==========
    categories.push({
      name: "Swallowing Assessment",
      priority: 6,
      required: formConfig.primaryFocus === 'swallowing',
      description: formConfig.primaryFocus === 'swallowing' ? "Comprehensive dysphagia assessment." : "Swallowing screen.",
      fields: [
        { name: "Swallowing Status", type: "select", required: true,
          options: ["No swallowing concerns", "Mild dysphagia", "Moderate dysphagia", "Severe dysphagia", "NPO - aspiration risk", "Enteral feeding only"] },
        { name: "Current Diet Level", type: "select", required: formConfig.primaryFocus === 'swallowing',
          options: formConfig.patientType === 'pediatric'
            ? ["Age-appropriate diet", "Soft solids", "Mashed/Ground", "Pureed Stage 2", "Pureed Stage 1", "NPO"]
            : ["Regular diet", "Mechanical soft", "Minced and moist", "Pureed", "NPO (nothing by mouth)", "Enteral nutrition only"] },
        { name: "Liquid Consistency", type: "select",
          options: ["Thin liquids", "Slightly thick (Level 1)", "Mildly thick (Level 2)", "Moderately thick (Level 3)", "Extremely thick (Level 4)", "No oral liquids"] },
        { name: "Oral Phase", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
          options: ["Within normal limits", "Mild impairment", "Moderate impairment", "Severe impairment"] },
        { name: "Pharyngeal Phase", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
          options: ["Within normal limits", "Mild impairment", "Moderate impairment", "Severe impairment"] },
        { name: "Signs of Aspiration", type: "select", required: true,
          options: ["No signs observed", "Coughing during/after swallowing", "Wet vocal quality", "Throat clearing", "Multiple signs present", "Silent aspiration suspected"] },
        { name: "Aspiration Risk Level", type: "select", required: formConfig.primaryFocus === 'swallowing',
          showFor: ['initial', 'recertification'] as AssessmentType[],
          options: ["Low risk", "Moderate risk", "High risk", "Very high risk - NPO recommended"] },
        { name: "Instrumental Evaluation Needed", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
          options: ["Not indicated", "MBSS recommended", "FEES recommended", "Already completed - see results", "Pending scheduling"] },
        { name: "Swallowing Notes", type: "textarea",
          placeholder: "Additional swallowing observations and recommendations" }
      ]
    });

    // ========== COGNITIVE-COMMUNICATION (adult/geriatric, or if primary focus) ==========
    if (formConfig.patientType !== 'pediatric' || formConfig.primaryFocus === 'cognitive') {
      categories.push({
        name: "Cognitive-Communication",
        priority: 7,
        required: formConfig.primaryFocus === 'cognitive',
        fields: [
          { name: "Orientation", type: "select",
            options: ["Oriented x4 (person, place, time, situation)", "Oriented x3", "Oriented x2", "Oriented x1", "Disoriented"] },
          { name: "Attention/Concentration", type: "select",
            options: ["Within normal limits", "Mild impairment", "Moderate impairment", "Severe impairment"] },
          { name: "Memory", type: "select",
            options: ["Within normal limits", "Mild impairment", "Moderate impairment", "Severe impairment"] },
          { name: "Executive Functioning", type: "select",
            options: ["Within normal limits", "Mild impairment", "Moderate impairment", "Severe impairment"] },
          { name: "Problem Solving", type: "select",
            options: ["Within normal limits", "Mild impairment", "Moderate impairment", "Severe impairment"] },
          { name: "Safety Awareness", type: "select",
            options: ["Good safety awareness", "Fair - needs occasional reminders", "Poor - needs frequent supervision", "Impaired - constant supervision needed"] },
          { name: "Cognitive-Communication Notes", type: "textarea",
            placeholder: "Additional cognitive-communication observations" }
        ]
      });
    }

    // ========== VOICE ASSESSMENT (if primary focus or optional) ==========
    if (formConfig.primaryFocus === 'voice' || formConfig.showOptionalSections) {
      categories.push({
        name: "Voice Assessment",
        priority: 8,
        required: formConfig.primaryFocus === 'voice',
        fields: [
          { name: "Overall Voice Quality", type: "select",
            options: ["Normal voice quality", "Hoarse", "Breathy", "Strained", "Weak", "Harsh", "Tremorous"] },
          { name: "Pitch", type: "select",
            options: ["Appropriate pitch", "Too high", "Too low", "Monotone", "Pitch breaks"] },
          { name: "Loudness", type: "select",
            options: ["Appropriate loudness", "Too loud", "Too soft", "Variable loudness", "Aphonic"] },
          { name: "Resonance", type: "select",
            options: ["Normal resonance", "Hypernasality", "Hyponasality", "Mixed nasality", "Cul-de-sac resonance"] },
          { name: "Vocal Fold Status", type: "select", showFor: ['initial', 'recertification'] as AssessmentType[],
            options: ["Unknown - ENT referral needed", "Normal per ENT", "Vocal fold paralysis/paresis", "Vocal nodules", "Vocal polyp", "Other pathology - see notes"] },
          { name: "Voice Notes", type: "textarea",
            placeholder: "Additional voice observations" }
        ]
      });
    }

    // ========== FLUENCY ASSESSMENT (if optional sections) ==========
    if (formConfig.showOptionalSections && assessmentType !== 'discharge') {
      categories.push({
        name: "Fluency Assessment",
        priority: 9,
        required: false,
        fields: [
          { name: "Fluency Status", type: "select",
            options: ["Normal fluency", "Typical dysfluencies", "Stuttering-like dysfluencies", "Cluttering characteristics", "Mixed fluency disorder"] },
          { name: "Frequency of Dysfluencies", type: "select",
            options: ["None observed", "Occasional (1-2%)", "Mild (3-5%)", "Moderate (6-10%)", "Severe (>10%)"] },
          { name: "Types of Dysfluencies", type: "textarea",
            placeholder: "Repetitions, prolongations, blocks, interjections" },
          { name: "Secondary Behaviors", type: "textarea",
            placeholder: "Physical tension, avoidance behaviors, word substitutions" },
          { name: "Fluency Notes", type: "textarea",
            placeholder: "Additional fluency observations" }
        ]
      });
    }

    // ========== AAC ASSESSMENT (if needed) ==========
    if (formConfig.showOptionalSections && assessmentType !== 'discharge') {
      categories.push({
        name: "AAC Assessment",
        priority: 10,
        required: false,
        fields: [
          { name: "AAC Candidacy", type: "select",
            options: ["Not a candidate - functional verbal communication", "AAC candidate - evaluation recommended", "Currently using low-tech AAC", "Currently using high-tech AAC", "AAC in development"] },
          { name: "Current Communication Methods", type: "textarea",
            placeholder: "Describe all current communication methods used" },
          { name: "AAC Access Method", type: "select",
            options: ["N/A", "Direct selection - finger", "Direct selection - eye gaze", "Switch scanning", "Head tracking", "Multiple access methods"] },
          { name: "AAC System Recommendations", type: "textarea",
            placeholder: "Recommended AAC systems, vocabulary needs, training requirements" }
        ]
      });
    }

    // ========== CAREGIVER/COMMUNICATION PARTNERS (all types) ==========
    categories.push({
      name: "Caregiver/Communication Partners",
      priority: 11,
      required: true,
      description: assessmentType === 'discharge' ? "Final caregiver status and training summary." : undefined,
      fields: [
        { name: "Primary Communication Partner", type: "text", required: true,
          placeholder: "Primary person patient communicates with" },
        { name: "Communication Partner Understanding", type: "select", required: true,
          options: ["Excellent understanding of patient's communication", "Good understanding", "Moderate understanding - needs strategies", "Limited understanding", "Poor understanding - significant support needed"] },
        { name: "Communication Strategies Used", type: "textarea",
          placeholder: "Current strategies family/caregivers use to communicate with patient" },
        { name: "Caregiver Training Needs", type: "textarea", showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Areas where caregiver education is needed" },
        { name: "Family Concerns", type: "textarea",
          placeholder: "Family concerns about communication, feeding, or safety" }
      ]
    });

    // ========== TREATMENT PLAN (initial and recert) ==========
    if (assessmentType !== 'discharge') {
      categories.push({
        name: "Treatment Plan",
        priority: 12,
        required: true,
        fields: [
          { name: "Plan Created By", type: "text", required: true, readonly: true,
            placeholder: "Auto-populated from current user (e.g., Sarah Johnson, SLP, MS)" },
          { name: "Plan Creation Date", type: "date", required: true, readonly: true,
            placeholder: "Auto-populated with today's date" },
          { name: "Primary Treatment Focus", type: "select", required: true,
            options: formConfig.patientType === 'pediatric'
              ? ["Speech sound production", "Language development", "Swallowing/Feeding", "Social communication/Pragmatics", "AAC", "Early intervention", "Multiple areas"]
              : ["Speech sound production", "Language recovery", "Swallowing safety", "Voice therapy", "Fluency", "Cognitive-communication", "AAC training", "Multiple areas"] },
          { name: "Short-Term Goals (60-90 days)", type: "textarea", required: true,
            placeholder: "List short-term goals (target 60-90 days from SOC). For each goal include:\n• Goal statement\n• Target date\n• Baseline measurement\n• Target measurement\n• How progress will be measured" },
          { name: "Long-Term Goals (120-180 days)", type: "textarea", required: true,
            placeholder: "List long-term goals (target 120-180 days from SOC). For each goal include:\n• Goal statement\n• Target date\n• Baseline measurement\n• Target measurement\n• How progress will be measured" },
          { name: "Treatment Approaches", type: "textarea", required: true,
            placeholder: formConfig.patientType === 'pediatric'
              ? "Specific therapy techniques: naturalistic intervention, drill-based practice, sensory-motor approach, etc."
              : "Specific therapy techniques and evidence-based approaches to be used" },
          { name: "Home Program", type: "textarea",
            placeholder: "Activities and exercises for patient/family to practice at home" },
          { name: "Service Frequency - Visits Per Week", type: "number", required: true,
            placeholder: "e.g., 2", min: 1, max: 7 },
          { name: "Service Frequency - Minutes Per Visit", type: "select", required: true,
            options: ["30", "45", "60", "75", "90", "120"] },
          { name: "Service Frequency - Total Hours Per Week", type: "text", readonly: true,
            placeholder: "Auto-calculated from visits and minutes" },
          { name: "Service Duration - Start Date", type: "date", required: true },
          { name: "Service Duration - Anticipated End Date", type: "date", required: true },
          { name: "Service Duration - Total Days", type: "text", readonly: true,
            placeholder: "Auto-calculated from start and end dates" },
          { name: "Certification Period", type: "select", required: true,
            options: ["60 days", "90 days", "120 days", "180 days"] },
          { name: "Rehabilitation Potential", type: "select", required: true,
            options: [
              "Excellent - expected to meet all goals",
              "Good - expected to meet most goals",
              "Fair - expected to make functional gains",
              "Guarded - limited expected progress",
              "Poor - maintenance/prevention focus"
            ]},
          { name: "Rehabilitation Potential Rationale", type: "textarea", required: true,
            placeholder: "Explain rationale for rehab potential based on patient's condition, cognitive status, caregiver support, compliance, medical stability" },
          { name: "Anticipated Discharge Plan", type: "textarea", required: true,
            showFor: ['initial'] as AssessmentType[],
            placeholder: "Describe anticipated discharge: expected to return to prior level of function, ongoing skilled care anticipated, specific discharge criteria, expected timeframe for discharge" },
          { name: "Discharge Criteria", type: "textarea",
            placeholder: "Criteria for discharge from ST services" },
          { name: "Coordination of Care", type: "textarea",
            placeholder: "Coordination with nursing, OT, PT, physician, school, etc." }
        ]
      });
    }

    // ========== ST ATTESTATION (all types) ==========
    categories.push({
      name: "ST Attestation",
      priority: 13,
      required: true,
      description: assessmentType === 'discharge' ? "Final attestation for discharge evaluation." : undefined,
      fields: [
        { name: "ST Summary", type: "textarea", required: true,
          placeholder: assessmentType === 'discharge'
            ? "Summary of ST episode of care, patient progress, and discharge status."
            : "Summary of key findings, communication limitations, and ST plan." },
        { name: "Skilled ST Justification", type: "textarea", required: assessmentType !== 'discharge',
          showFor: ['initial', 'recertification'] as AssessmentType[],
          placeholder: "Justify why skilled ST services are required (complexity, safety concerns, skilled techniques)" },
        { name: "Rehab Potential", type: "select", required: assessmentType !== 'discharge',
          showFor: ['initial', 'recertification'] as AssessmentType[],
          options: ["Excellent - expected to meet all goals", "Good - expected to meet most goals", "Fair - expected to make functional gains", "Guarded - limited expected progress", "Poor - maintenance/compensatory focus"] },
        { name: "ST Signature Attestation", type: "select", required: true,
          options: assessmentType === 'discharge'
            ? ["I attest this discharge evaluation is complete and accurate", "Evaluation pending additional information"]
            : ["I attest this evaluation is complete and accurate", "Evaluation pending additional information"] }
      ]
    });

    return categories.sort((a, b) => a.priority - b.priority);
  };

  // Form data state management
  const [formData, setFormData] = useState(() => {
    if (existingData && typeof existingData === 'object' && Object.keys(existingData).length > 0) {
      // Load secondary ICD-10 codes from existing data
      const existingSecondary = existingData.categories?.['Diagnosis and Coding-Secondary ICD-10 Codes'];
      if (existingSecondary && typeof existingSecondary === 'string') {
        const codes = splitIcd10Field(existingSecondary);
        setSecondaryICD10Codes(codes);
      }
      return existingData;
    }

    const categories = {};
    getAssessmentCategories().forEach(category => {
      category.fields.forEach(field => {
        categories[`${category.name}-${field.name}`] = "";
      });
    });
    
    return {
      id: "",
      date: new Date().toISOString().split('T')[0],
      provider: "Current User, SLP",
      status: "Pending",
      category: "ST",
      lastUpdated: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true }),
      categories: categories,
      formConfig: formConfig
    };
  });
  
  useEffect(() => {
    // Only run this effect once when existingData is first loaded
    // Skip if we've already loaded the initial data to prevent resetting user changes
    if (initialLoadCompleteRef.current) {
      return;
    }

    if (existingData && typeof existingData === 'object' && Object.keys(existingData).length > 0) {
      setFormData(existingData);
      // Merge existing formConfig with defaults to ensure all fields are present
      const mergedConfig = withPediatricFirstConfig({ ...defaultFormConfig, ...existingData.formConfig });
      // Normalize assessment type from backend format to internal format
      mergedConfig.assessmentType = normalizeAssessmentType(mergedConfig.assessmentType);
      setFormConfig(mergedConfig);
      // Mark initial load as complete after loading existing data
      initialLoadCompleteRef.current = true;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- defaultFormConfig is a fresh object every render; only a new existingData should reload the form
  }, [existingData]);

  useEffect(() => {
    const categories = {};
    getAssessmentCategories().forEach(category => {
      category.fields.forEach(field => {
        const fieldKey = `${category.name}-${field.name}`;
        categories[fieldKey] = formData.categories?.[fieldKey] || "";
      });
    });
    
    setFormData(prev => ({
      ...prev,
      categories: { ...prev.categories, ...categories },
      formConfig: formConfig
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs only when the form type fields change; adding formConfig/formData would rebuild the categories on every edit
  }, [formConfig.assessmentType, formConfig.patientType, formConfig.settingType, formConfig.primaryFocus, formConfig.showOptionalSections]);

  // Auto-calculation effects - MUST BE AFTER formData DECLARATION
  const serviceVisitsPerWeek = formData.categories?.['Treatment Plan-Service Frequency - Visits Per Week'];
  const serviceMinutesPerVisit = formData.categories?.['Treatment Plan-Service Frequency - Minutes Per Visit'];
  useEffect(() => {
    // Auto-calculate total hours per week
    const visitsPerWeek = parseInt(serviceVisitsPerWeek || '0');
    const minutesPerVisit = parseInt(serviceMinutesPerVisit || '0');
    
    if (visitsPerWeek > 0 && minutesPerVisit > 0) {
      const totalHours = (visitsPerWeek * minutesPerVisit) / 60;
      setFormData(prev => ({
        ...prev,
        categories: {
          ...prev.categories,
          'Treatment Plan-Service Frequency - Total Hours Per Week': `${totalHours.toFixed(1)} hours`
        }
      }));
    }
  }, [serviceVisitsPerWeek, serviceMinutesPerVisit]);

  // Auto-calculate duration in days
  const serviceStartDate = formData.categories?.['Treatment Plan-Service Duration - Start Date'];
  const serviceEndDate = formData.categories?.['Treatment Plan-Service Duration - Anticipated End Date'];
  useEffect(() => {
    const startDate = serviceStartDate;
    const endDate = serviceEndDate;
    
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const days = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
      
      setFormData(prev => ({
        ...prev,
        categories: {
          ...prev.categories,
          'Treatment Plan-Service Duration - Total Days': `${days} days`
        }
      }));
    }
  }, [serviceStartDate, serviceEndDate]);

  // Auto-populate plan creator and date on form initialization
  useEffect(() => {
    if (!editingId && !formData.categories?.['Treatment Plan-Plan Created By']) {
      setFormData(prev => ({
        ...prev,
        categories: {
          ...prev.categories,
          'Treatment Plan-Plan Created By': formData.provider,
          'Treatment Plan-Plan Creation Date': new Date().toISOString().split('T')[0]
        }
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- auto-fill only on open/provider change; adding the field would re-fill it after the user clears it
  }, [editingId, formData.provider]);

  const handleInputChange = (fieldKey, value) => {
    setFormData(prev => ({
      ...prev,
      categories: { ...(prev.categories || {}), [fieldKey]: value }
    }));
  };

  const handleAddSecondaryICD10 = () => {
    if (selectedSecondaryICD10 && !secondaryICD10Codes.includes(selectedSecondaryICD10)) {
      const updatedCodes = [...secondaryICD10Codes, selectedSecondaryICD10];
      setSecondaryICD10Codes(updatedCodes);
      setSelectedSecondaryICD10('');
      // Update form data
      setFormData(prev => ({
        ...prev,
        categories: { ...(prev.categories || {}), 'Diagnosis and Coding-Secondary ICD-10 Codes': joinIcd10Field(updatedCodes) }
      }));
    }
  };

  const handleRemoveSecondaryICD10 = (code) => {
    const updatedCodes = secondaryICD10Codes.filter(c => c !== code);
    setSecondaryICD10Codes(updatedCodes);
    // Update form data
    setFormData(prev => ({
      ...prev,
      categories: { ...(prev.categories || {}), 'Diagnosis and Coding-Secondary ICD-10 Codes': joinIcd10Field(updatedCodes) }
    }));
  };

  const handleConfigChange = (key, value) => {
    setFormConfig(prev => ({ ...prev, [key]: value }));
  };

  const toggleSection = (sectionName) => {
    setCollapsedSections(prev => ({ ...prev, [sectionName]: !prev[sectionName] }));
  };

  const toggleAllSections = (collapse: boolean) => {
    const newState: any = {};
    // Include all currently rendered sections from getAssessmentCategories
    getAssessmentCategories().forEach(category => {
      newState[category.name] = collapse;
    });
    // Also update any sections that exist in current state but aren't in categories
    Object.keys(collapsedSections).forEach(key => {
      if (!(key in newState)) {
        newState[key] = collapse;
      }
    });
    setCollapsedSections(newState);
    setConfigSectionCollapsed(collapse);
  };

  const getCategoryColorClasses = (categoryName) => {
    const colorMap = {
      "Recertification Summary": { bg: "bg-amber-50", border: "border-amber-200", hover: "hover:bg-amber-100" },
      "Discharge Summary": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" },
      "Diagnosis and Coding": { bg: "bg-blue-50", border: "border-blue-200", hover: "hover:bg-blue-100" },
      "Functional Limitations": { bg: "bg-red-50", border: "border-red-200", hover: "hover:bg-red-100" },
      "Mental Status": { bg: "bg-purple-50", border: "border-purple-200", hover: "hover:bg-purple-100" },
      "Physician Orders and Certification": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" },
      "Home Health Certification": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" },
      "Patient History": { bg: "bg-gray-50", border: "border-gray-200", hover: "hover:bg-gray-100" },
      "Pediatric Information": { bg: "bg-purple-50", border: "border-purple-200", hover: "hover:bg-purple-100" },
      "Pediatric Language Development": { bg: "bg-purple-50", border: "border-purple-200", hover: "hover:bg-purple-100" },
      "Pediatric PDN Speech Therapy": { bg: "bg-red-50", border: "border-red-200", hover: "hover:bg-red-100" },
      "Speech Assessment": { bg: "bg-purple-50", border: "border-purple-200", hover: "hover:bg-purple-100" },
      "Language Assessment": { bg: "bg-blue-50", border: "border-blue-200", hover: "hover:bg-blue-100" },
      "Swallowing Assessment": { bg: "bg-orange-50", border: "border-orange-200", hover: "hover:bg-orange-100" },
      "Cognitive-Communication": { bg: "bg-pink-50", border: "border-pink-200", hover: "hover:bg-pink-100" },
      "Voice Assessment": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" },
      "Fluency Assessment": { bg: "bg-red-50", border: "border-red-200", hover: "hover:bg-red-100" },
      "AAC Assessment": { bg: "bg-yellow-50", border: "border-yellow-200", hover: "hover:bg-yellow-100" },
      "Caregiver/Communication Partners": { bg: "bg-teal-50", border: "border-teal-200", hover: "hover:bg-teal-100" },
      "Treatment Plan": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" },
      "ST Attestation": { bg: "bg-green-50", border: "border-green-200", hover: "hover:bg-green-100" }
    };
    return colorMap[categoryName] || { bg: "bg-white", border: "border-gray-200", hover: "hover:bg-gray-50" };
  };

  const getCategoryIcon = (categoryName) => {
    const icons = {
      "Recertification Summary": <RefreshCw size={18} className="mr-2 text-amber-600" />,
      "Discharge Summary": <LogOut size={18} className="mr-2 text-green-600" />,
      "Diagnosis and Coding": <FileBarChart size={18} className="mr-2 text-blue-600" />,
      "Home Health Certification": <Shield size={18} className="mr-2 text-green-600" />,
      "Patient History": <FileText size={18} className="mr-2 text-gray-700" />,
      "Pediatric Information": <Baby size={18} className="mr-2 text-purple-600" />,
      "Pediatric Language Development": <MessageCircle size={18} className="mr-2 text-blue-600" />,
      "Pediatric PDN Speech Therapy": <Heart size={18} className="mr-2 text-red-600" />,
      "Speech Assessment": <Mic size={18} className="mr-2 text-purple-600" />,
      "Language Assessment": <MessageCircle size={18} className="mr-2 text-blue-600" />,
      "Swallowing Assessment": <Utensils size={18} className="mr-2 text-orange-600" />,
      "Cognitive-Communication": <Brain size={18} className="mr-2 text-pink-600" />,
      "Voice Assessment": <Volume2 size={18} className="mr-2 text-green-600" />,
      "Fluency Assessment": <Activity size={18} className="mr-2 text-red-600" />,
      "AAC Assessment": <Zap size={18} className="mr-2 text-yellow-600" />,
      "Caregiver/Communication Partners": <Users size={18} className="mr-2 text-teal-600" />,
      "Treatment Plan": <Target size={18} className="mr-2 text-green-600" />,
      "ST Attestation": <CheckCircle size={18} className="mr-2 text-green-600" />,
      "Functional Limitations": <AlertTriangle size={18} className="mr-2 text-red-600" />,
      "Mental Status": <Activity size={18} className="mr-2 text-purple-600" />,
      "Physician Orders and Certification": <FileText size={18} className="mr-2 text-green-600" />
    };
    return icons[categoryName] || <FileText size={18} className="mr-2 text-gray-700" />;
  };

  const validateForm = () => {
    const errors = [];
    const categories = getAssessmentCategories();
    
    categories.forEach(section => {
      section.fields.forEach(field => {
        if (shouldShowField(field) && field.required && !formData.categories?.[`${section.name}-${field.name}`]) {
          errors.push(`${field.name} is required`);
        }
      });
    });
    
    // NEW VALIDATION RULES:
    
    // 1. Physician signature timing (if verbal order received)
    const verbalOrderDate = formData.categories?.['Physician Orders and Certification-Verbal Order Date'];
    const physicianSignatureDate = formData.categories?.['Physician Orders and Certification-Physician Signature Date'];
    
    if (verbalOrderDate && physicianSignatureDate) {
      const daysDiff = Math.floor((new Date(physicianSignatureDate).getTime() - new Date(verbalOrderDate).getTime()) / (1000 * 60 * 60 * 24));
      if (daysDiff > 30) {
        errors.push('WARNING: Physician signature should be obtained within 30 days of verbal order (CMS requirement)');
      }
    }
    
    // 2. Medical necessity minimum length
    const clinicalComplexity = formData.categories?.['Diagnosis and Coding-Medical Necessity - Clinical Complexity'] || '';
    const skilledCare = formData.categories?.['Diagnosis and Coding-Medical Necessity - Skilled Care Requirements'] || '';
    const functionalImpact = formData.categories?.['Diagnosis and Coding-Medical Necessity - Functional Impact'] || '';
    const expectedOutcomes = formData.categories?.['Diagnosis and Coding-Medical Necessity - Expected Outcomes'] || '';
    
    const totalMedNecLength = clinicalComplexity.length + skilledCare.length + functionalImpact.length + expectedOutcomes.length;
    
    if (totalMedNecLength < 500 && formConfig.assessmentType !== 'discharge') {
      errors.push('Medical necessity documentation too brief - CMS audits require detailed justification (minimum 500 characters across all sections)');
    }
    
    // 3. Homebound criteria
    const homeboundCriteria = formData.categories?.['Home Health Certification-Homebound Status Criteria (Check all that apply)'];
    if (formConfig.settingType === 'home-health' && formConfig.assessmentType !== 'discharge') {
      if (!homeboundCriteria || homeboundCriteria.length === 0) {
        errors.push('Must select at least one homebound criterion');
      }
    }
    
    // 4. Functional limitations
    const funcLimitations = formData.categories?.['Functional Limitations-Functional Limitation Categories'];
    if (formConfig.assessmentType !== 'discharge' && (!funcLimitations || funcLimitations.length === 0)) {
      errors.push('Must select at least one functional limitation');
    }
    
    // Phase 19 F23: test results or the reason none was given (initial / recertification)
    errors.push(...therapyTestErrors(formConfig.assessmentType, pickTherapyTestFields(formData)));

    return errors;
  };

  // Payload passed to onSave (Save, Submit and autosave all use it)
  const buildSaveData = () => ({
    ...formData,
    formConfig: withPediatricFirstConfig(formConfig),
    status: 'Draft',
    lastUpdated: new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric', hour12: true })
  });

  // Save keeps a DRAFT and never blocks on required fields; Submit runs validate() (Phase 4)
  const handleSave = () => {
    onSave(buildSaveData());
  };


  // Phase 19 F23: standardized tests are saved at the top level of data, next to categories
  const handleTherapyTestChange = (patch: Partial<TherapyTestFieldsDto>) => {
    setFormData(prev => ({ ...prev, ...patch }));
  };
  const showTests = formConfig.assessmentType !== 'discharge';
  const renderedCategories = getAssessmentCategories();
  // Shown just before the first section after the developmental section (priority 3.5)
  const testsAnchor = showTests ? renderedCategories.findIndex(c => c.priority > 3.5) : -1;
  const testsSection = showTests ? (
    <StandardizedTestSection
      discipline="ST"
      value={pickTherapyTestFields(formData)}
      onChange={handleTherapyTestChange}
      readOnly={isReadOnly}
      legacy={legacyEntries(formData.categories, LEGACY_TEST_KEYS)}
      collapsed={collapsedSections['Standardized Tests'] === true}
      onToggle={() => toggleSection('Standardized Tests')}
    />
  ) : null;

  useImperativeHandle(ref, () => ({
    triggerSave: () => handleSave(),
    validate: () => validateForm(),
    getSaveData: () => buildSaveData(),
    getSnapshot: () => JSON.stringify(formData),
  }));

  return (
    <div className="space-y-6">
      <div className="bg-white text-left">

        {/* Header */}
        <div className="border-b border-gray-200 pb-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => toggleAllSections(!Object.values(collapsedSections).every(v => v))}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md border border-gray-300 transition-colors"
            >
              {Object.values(collapsedSections).every(v => v) ? (
                <>
                  <ChevronsDown size={16} />
                  <span>Expand All</span>
                </>
              ) : (
                <>
                  <ChevronsUp size={16} />
                  <span>Collapse All</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Assessment Type and Configuration - Collapsible */}
        <div className="bg-gray-50 rounded-lg mb-6 border border-gray-200">
          {/* Collapsible Header */}
          <div
            className="flex items-center justify-between cursor-pointer p-4 hover:bg-gray-100 rounded-t-lg transition-colors"
            onClick={() => setConfigSectionCollapsed(!configSectionCollapsed)}
          >
            <div className="flex items-center gap-3">
              <FileEdit size={18} className="text-blue-600" />
              <div>
                <h4 className="text-sm font-medium text-gray-700">Evaluation Configuration</h4>
                {configSectionCollapsed && formConfig.assessmentType && ASSESSMENT_TYPES[formConfig.assessmentType] && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {ASSESSMENT_TYPES[formConfig.assessmentType].label} • {patientTypeLabel(formConfig.patientType)}
                  </p>
                )}
              </div>
            </div>
            <div>
              {configSectionCollapsed ? <ChevronDown size={20} className="text-gray-500" /> : <ChevronUp size={20} className="text-gray-500" />}
            </div>
          </div>

          {/* Collapsible Content */}
          <div className={configSectionCollapsed ? 'hidden' : 'p-4 pt-0'}>
          <h4 className="text-sm font-medium text-gray-700 mb-3">Evaluation Type</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            {(Object.keys(ASSESSMENT_TYPES) as AssessmentType[]).map((type) => {
              const config = ASSESSMENT_TYPES[type];
              const isSelected = formConfig.assessmentType === type;
              const colorClasses = {
                blue: isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white',
                amber: isSelected ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300 bg-white',
                green: isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300 bg-white'
              };
              const textColorClasses = {
                blue: isSelected ? 'text-blue-600' : 'text-gray-500',
                amber: isSelected ? 'text-amber-600' : 'text-gray-500',
                green: isSelected ? 'text-green-600' : 'text-gray-500'
              };
              
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => (!editingId || assessmentStatus === 'DRAFT') && !isReadOnly && handleConfigChange('assessmentType', type)}
                  disabled={(!!editingId && assessmentStatus !== 'DRAFT') || isReadOnly}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${colorClasses[config.color]} ${editingId && assessmentStatus !== 'DRAFT' ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="flex items-center mb-1">
                    <span className={`mr-2 ${textColorClasses[config.color]}`}>{config.icon}</span>
                    <span className={`font-medium text-sm ${isSelected ? `text-${config.color}-900` : 'text-gray-700'}`}>
                      {config.label}
                    </span>
                  </div>
                  <p className={`text-xs ${isSelected ? `text-${config.color}-700` : 'text-gray-500'}`}>
                    {config.description}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Assessment Type Notice */}
          {formConfig.assessmentType === 'recertification' && (
            <div className="p-3 rounded-lg flex items-start mb-4 bg-amber-50 border border-amber-200">
              <RefreshCw size={18} className="text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-amber-800">
                <strong>Recertification Mode:</strong> Birth history and baseline fields are hidden. 
                Focus on documenting progress toward goals and justifying continued ST services.
              </div>
            </div>
          )}
          
          {formConfig.assessmentType === 'discharge' && (
            <div className="p-3 rounded-lg flex items-start mb-4 bg-green-50 border border-green-200">
              <LogOut size={18} className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-green-800">
                <strong>Discharge Mode:</strong> Treatment planning fields are hidden. 
                Focus on documenting outcomes, communication status, and recommendations.
              </div>
            </div>
          )}

          {/* Form Configuration */}
          <h4 className="text-sm font-medium text-gray-700 mb-3 mt-4">Evaluation Configuration</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
              <input type="date" className="w-full p-2 border border-gray-300 rounded text-base"
                disabled={isReadOnly}
                value={formData.date}
                onChange={(e) => setFormData({...formData, date: e.target.value})} />
            </div>
            <AdultTransitionToggle disabled={isReadOnly} patientType={formConfig.patientType}
              onChange={(patientType) => handleConfigChange('patientType', patientType)} />
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Primary Focus</label>
              <select disabled={isReadOnly} className="w-full p-2 border border-gray-300 rounded text-base" value={formConfig.primaryFocus}
                onChange={(e) => handleConfigChange('primaryFocus', e.target.value)}>
                <option value="speech-language">Speech-Language</option>
                <option value="swallowing">Swallowing</option>
                <option value="cognitive">Cognitive</option>
                <option value="voice">Voice</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Provider</label>
              <input type="text" className="w-full p-2 border border-gray-300 rounded text-base"
                disabled={isReadOnly}
                value={formData.provider}
                onChange={(e) => setFormData({...formData, provider: e.target.value})} />
            </div>
          </div>
          
          {/* Optional sections toggle */}
          <div className="mt-4">
            <label className="flex items-center text-sm text-gray-600">
              <input type="checkbox" disabled={isReadOnly} className="mr-2" checked={formConfig.showOptionalSections}
                onChange={(e) => handleConfigChange('showOptionalSections', e.target.checked)} />
              Show Optional Sections (Voice, Fluency, AAC)
            </label>
          </div>
          
          {/* Pediatric PDN Notice */}
          {formConfig.patientType === 'pediatric' && formConfig.settingType === 'home-health' && (
            <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg flex items-start">
              <Baby size={18} className="text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-purple-800">
                <strong>Pediatric Home Health ST:</strong> Includes specialized language development, feeding/swallowing, and PDN-specific documentation for Texas Medicaid.
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Main form content - categories */}
        {renderedCategories.map((category, categoryIndex) => {
          const visibleFields = category.fields.filter(field => shouldShowField(field));
          if (visibleFields.length === 0) return categoryIndex === testsAnchor ? <div key={categoryIndex}>{testsSection}</div> : null;
          
          const colors = getCategoryColorClasses(category.name);
          
          return (
            <div key={categoryIndex} className="mb-6">
              {categoryIndex === testsAnchor && testsSection}
              <div className={`rounded-lg border ${colors.border} shadow-sm ${colors.bg}`}>
                <div className={`flex items-center justify-between cursor-pointer p-4 ${colors.hover} rounded-t-lg transition-colors`} onClick={() => toggleSection(category.name)}>
                  <div className="flex items-center">
                    {getCategoryIcon(category.name)}
                    <h4 className="text-base font-medium text-gray-900">{category.name}</h4>
                    {category.required && <span className="ml-2 text-xs text-red-600 font-medium">Required</span>}
                  </div>
                  <div>
                    {collapsedSections[category.name] ? <ChevronDown size={16} className="text-gray-500" /> : <ChevronUp size={16} className="text-gray-500" />}
                  </div>
                </div>
                
                {category.description && !collapsedSections[category.name] && (
                  <p className="text-sm text-gray-600 px-4 pb-2 border-b border-gray-100">{category.description}</p>
                )}

                <div className={`p-4 ${collapsedSections[category.name] ? 'hidden' : 'block'}`}>
                {visibleFields.map((field, fieldIndex) => {
                  const fieldKey = `${category.name}-${field.name}`;
                  const placeholder = getPlaceholder(field);
                  
                  return (
                    <div key={fieldIndex} className="mb-4 min-w-0">
                      <label className="block text-sm font-medium text-gray-700 mb-1 text-left">
                        {field.name}
                        {field.required && <span className="ml-1 text-red-600">*</span>}
                      </label>
                      
                      {field.type === 'select' && field.name === 'Primary ICD-10 Code' && (
                        <div>
                          <ICDSelect
                            value={formData.categories?.[fieldKey] || ''}
                            onChange={(val) => handleInputChange(fieldKey, val)}
                            placeholder={`Select ${field.name}...`}
                            className={`min-w-0 ${field.required && !formData.categories?.[fieldKey] ? 'ring-1 ring-red-300' : ''}`}
                            disabled={isReadOnly}
                          />
                        </div>
                      )}
                      {field.type === 'select' && field.name !== 'Primary ICD-10 Code' && (
                        <select
                          disabled={isReadOnly}
                          className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          value={formData.categories?.[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                        >
                          <option value="">Select {field.name}...</option>
                          {field.options?.map((option, idx) => <option key={idx} value={option}>{option}</option>)}
                        </select>
                      )}
                      
                      {field.type === 'date' && (
                        <input type="date"
                          disabled={isReadOnly}
                          className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          value={formData.categories?.[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)} />
                      )}
                      
                      {field.type === 'textarea' && (
                        <textarea rows={3}
                          disabled={isReadOnly}
                          className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          value={formData.categories?.[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          placeholder={placeholder} />
                      )}
                      
                      {field.type === 'text' && (
                        <input type="text"
                          disabled={isReadOnly}
                          className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          value={formData.categories?.[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          placeholder={placeholder}
                          readOnly={field.readonly} />
                      )}

                      {field.type === 'number' && (
                        <input type="number"
                          disabled={isReadOnly}
                          className={`w-full p-2 border rounded text-base ${field.required && !formData.categories?.[fieldKey] ? 'border-red-300 bg-red-50' : 'border-gray-300'}`}
                          value={formData.categories?.[fieldKey] || ''}
                          onChange={(e) => handleInputChange(fieldKey, e.target.value)}
                          placeholder={placeholder}
                          readOnly={field.readonly}
                          min={field.min}
                          max={field.max} />
                      )}

                      {field.type === 'checkbox-group' && (
                        <div className="space-y-2">
                          {field.options?.map((option, idx) => {
                            const currentValues = formData.categories?.[fieldKey] ? 
                              (Array.isArray(formData.categories[fieldKey]) ? formData.categories[fieldKey] : formData.categories[fieldKey].split(',')) : [];
                            return (
                              <label key={idx} className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  className="rounded border-gray-300"
                                  checked={currentValues.includes(option)}
                                  onChange={(e) => {
                                    const newValues = e.target.checked
                                      ? [...currentValues, option]
                                      : currentValues.filter(v => v !== option);
                                    handleInputChange(fieldKey, newValues);
                                  }}
                                />
                                <span className="text-sm text-gray-700">{option}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}

                      {field.type === 'custom-icd10-list' && (
                        <div className="space-y-3">
                          <div className="flex flex-col sm:flex-row gap-2 min-w-0">
                            <ICDSelect
                              exclude={secondaryICD10Codes}
                              value={selectedSecondaryICD10}
                              onChange={(val) => setSelectedSecondaryICD10(val)}
                              placeholder="Select ICD-10 code..."
                              className="flex-1 w-full"
                              disabled={isReadOnly}
                            />
                            <PrimaryButton
                              type="button"
                              onClick={handleAddSecondaryICD10}
                              disabled={!selectedSecondaryICD10 || isReadOnly}
                              className="whitespace-nowrap w-full sm:w-auto"
                            >
                              Add ICD Code
                            </PrimaryButton>
                          </div>
                          {secondaryICD10Codes.length > 0 && (
                            <div className="border border-gray-200 rounded p-3 bg-gray-50">
                              <p className="text-sm font-medium text-gray-700 mb-2">Added Secondary Diagnoses:</p>
                              <ul className="space-y-2">
                                {secondaryICD10Codes.map((code, idx) => (
                                  <li key={idx} className="flex items-center justify-between bg-white p-2 rounded border border-gray-200">
                                    <span className="text-sm">{code}</span>
                                    <button
                                      type="button"
                                      onClick={() => handleRemoveSecondaryICD10(code)}
                                      disabled={isReadOnly}
                                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                                    >
                                      Remove
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            </div>
          );
        })}
        {testsAnchor === -1 && testsSection}
        
      </div>
    </div>
  );
  }
);
STAssessmentForm.displayName = 'STAssessmentForm';

export default STAssessmentForm;
